import classNames from 'classnames';
import moment from 'moment';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Voucher } from '../../../api/types/voucher';
import infoIcon from '../../../assets/images/icon-outline-info.svg';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { getTimezone } from '../../../selectors/hubSelector';
import { DATE_FORMAT_TYPE_15 } from '../../../util/constants';

export type VoucherListItemProps = {
  voucher: Voucher;
};

export const VoucherListItem = ({ voucher }: VoucherListItemProps) => {
  const { status, expiryDate, usedDate, voucherType } = voucher;
  const timezone = useSelector(getTimezone);

  const getStatusColor = useCallback((status: string): string => {
    switch (status) {
      default:
      case 'Active':
        return 'bg-green-2';
      case 'Expired':
        return 'bg-[#F1BC0080]';
      case 'Used':
        return 'bg-[#E4DED7]';
    }
  }, []);

  const getCardStyles = useCallback((status: string): string => {
    switch (status) {
      default:
      case 'Active':
        return 'border-grey-3 border shadow-md';
      case 'Expired':
        return 'border-2 border-dashed border-[#E4DED7]';
      case 'Used':
        return 'bg-[#EFE9E1] border-[#E4DED7] shadow-md';
    }
  }, []);

  const getTooltipContent = useCallback((type: string): string => {
    switch (type) {
      case 'Holiday':
        return 'Issued for a cancelled school holiday session';
      case 'Loyalty':
        return 'A reward for your continued learning';
      case 'Service Recovery':
        return 'Complimentary voucher to make things right';
      default:
        return '';
    }
  }, []);

  const getVoucherType = useCallback(() => {
    if (voucherType === 'Service Recovery') {
      return 'Service voucher';
    }
    return `${voucherType} voucher`;
  }, [voucherType]);

  return (
    <div className={classNames('poppins flex flex-row justify-between gap-4 rounded-lg p-5', getCardStyles(status))}>
      <div className="flex flex-grow flex-col gap-3">
        <div className="flex flex-col justify-between gap-2 gap-y-2 lg:flex-row">
          <p className="flex flex-row flex-wrap items-center gap-2 text-lg">
            <span className="flex flex-row items-center gap-1">
              {getVoucherType()}
              {voucherType && (
                <Tooltip content={getTooltipContent(voucherType)} tooltipClassName="!w-[200px]">
                  <img src={infoIcon} alt="info" className="h-6 w-6" />
                </Tooltip>
              )}
            </span>
            <span className="hidden text-lg text-grey-4 lg:block">&#8226; 60 minutes</span>
            <span className="text-base text-grey-4 lg:hidden">60 minutes</span>
          </p>
        </div>
        <p className="flex flex-row flex-wrap gap-x-2 text-base">
          {status === 'Active' && (
            <>
              <span>Valid until </span>
              <span className="font-semibold">{moment(expiryDate).tz(timezone).format(DATE_FORMAT_TYPE_15)}</span>
            </>
          )}
          {status === 'Expired' && (
            <>
              <span>Expired on </span>
              <span className="font-semibold">{moment(expiryDate).tz(timezone).format(DATE_FORMAT_TYPE_15)}</span>
            </>
          )}
          {status === 'Used' && (
            <>
              <span>Used on </span>
              <span className="font-semibold">{moment(usedDate).tz(timezone).format(DATE_FORMAT_TYPE_15)}</span>
            </>
          )}
        </p>
      </div>
      <div>
        <p
          className={classNames(
            'flex items-center rounded-full px-2 py-1 text-xs font-semibold',
            getStatusColor(status)
          )}
        >
          {status}
        </p>
      </div>
    </div>
  );
};
